"use client";

import { useState, useEffect, useMemo } from "react";
import Link from "next/link";

interface StoreBrand {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  description: string | null;
  website: string | null;
  country: string | null;
  currency: string | null;
  product_count: number;
}

export default function StoreDirectoryPage() {
  const [brands, setBrands] = useState<StoreBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");

  useEffect(() => {
    fetch("/api/store-directory")
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed");
        return res.json();
      })
      .then((data) => setBrands(data.brands || []))
      .catch(() => setBrands([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set<string>();
    brands.forEach((b) => {
      if (b.description) {
        // Simple category extraction from description keywords
      }
    });
    return ["All", "Tops", "Dresses", "Shirts", "Pants", "Activewear"];
  }, []);

  const filtered = useMemo(() => {
    return brands.filter((b) => {
      if (search && !b.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [brands, search]);

  return (
    <>
      <header className="site-header">
        <Link href="/" className="site-header__brand">DressingRoom</Link>
        <nav className="site-header__nav">
          <Link href="/profile" className="site-header__link">Profile</Link>
          <Link href="/store" className="site-header__link site-header__link--active">Stores</Link>
          <Link href="/catalog" className="site-header__link">Catalog</Link>
          <Link href="/saved" className="site-header__link">Saved</Link>
        </nav>
      </header>

      <div className="store-directory">
        <div className="store-directory__hero">
          <h1 className="store-directory__title">Discover Caribbean Brands</h1>
          <p className="store-directory__subtitle">
            Shop premium island fashion with AI-powered virtual try-on
          </p>
        </div>

        <div className="store-directory__search">
          <svg className="catalog-search__icon" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="7" cy="7" r="5" /><path d="M11 11l3 3" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search brands…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="store-directory__search-input"
          />
        </div>

        {loading ? (
          <div className="store-directory__grid">
            {[1, 2, 3].map((i) => (
              <div key={i} className="store-card store-card--skeleton">
                <div className="store-card__banner store-card__banner--placeholder" />
                <div className="store-card__body">
                  <div className="store-card__name-placeholder" />
                  <div className="store-card__desc-placeholder" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <h3 className="empty-state__title">No Stores Found</h3>
            <p className="empty-state__text">
              {search ? "Try a different search term." : "No brands have set up their store yet."}
            </p>
          </div>
        ) : (
          <div className="store-directory__grid">
            {filtered.map((brand) => (
              <Link
                key={brand.id}
                href={`/store/${brand.slug}`}
                className="store-card"
              >
                <div
                  className="store-card__banner"
                  style={!brand.logo_url ? {
                    background: "linear-gradient(135deg, #f5e6d3, #e8d5b7)",
                  } : undefined}
                >
                  {brand.logo_url ? (
                    /* eslint-disable-next-line @next/next/no-img-element */
                    <img src={brand.logo_url} alt={brand.name} className="store-card__logo" />
                  ) : (
                    <span className="store-card__logo-placeholder">
                      {brand.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="store-card__body">
                  <h3 className="store-card__name">{brand.name}</h3>
                  <p className="store-card__desc">
                    {brand.description || "Premium Caribbean fashion brand"}
                  </p>
                  <div className="store-card__meta">
                    {brand.country && (
                      <span className="store-card__country">📍 {brand.country}</span>
                    )}
                    <span className="store-card__count">
                      {brand.product_count} {brand.product_count === 1 ? "piece" : "pieces"}
                    </span>
                  </div>
                  <div className="store-card__cta">
                    Visit Store →
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="store-powered">
          Powered by <strong>DressingRoom</strong>
        </div>
      </div>

      <nav className="mobile-nav">
        <Link href="/profile" className="mobile-nav__btn">
          <svg className="mobile-nav__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="10" cy="7" r="3" /><path d="M4 17c0-3 3-5 6-5s6 2 6 5" />
          </svg>
          Profile
        </Link>
        <Link href="/store" className="mobile-nav__btn mobile-nav__btn--active">
          <svg className="mobile-nav__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="6" height="6" /><rect x="11" y="3" width="6" height="6" /><rect x="3" y="11" width="6" height="6" /><rect x="11" y="11" width="6" height="6" />
          </svg>
          Stores
        </Link>
        <Link href="/catalog" className="mobile-nav__btn">
          <svg className="mobile-nav__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M3 5h14l-1.5 9a2 2 0 01-2 1.7H6.5a2 2 0 01-2-1.7L3 5z" /><path d="M7 5V3h6v2" />
          </svg>
          Catalog
        </Link>
        <Link href="/saved" className="mobile-nav__btn">
          <svg className="mobile-nav__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M5 3h10v14l-5-3-5 3V3z" />
          </svg>
          Saved
        </Link>
      </nav>
    </>
  );
}
